import { memo } from "react";

import { BaseApp, Order, Route, User } from "@web-app/types";

import { GroupParameter } from "../useOrderSidebar";

import ConnectionsDriversView from "./ConnectionsDriversView";
import ConnectionsView from "./ConnectionsView";
import EmptyState from "./EmptyState";
import ListView from "./ListView";
import LocationsView from "./LocationsView";
import StatesView from "./StatesView";

interface ListProps {
  appsMap: Map<string, BaseApp>;
  assigneesMap: Map<string, User>;
  autoAssignSearchingIds: Set<string>;
  groupParameter: GroupParameter;
  isAppsLoading?: boolean;
  isAssigneesLoading?: boolean;
  isLoading: boolean;

  onCopy: (orderId: string) => void;
  onDelete: (orderId: string) => void;
  onSelectExternalProvider: (
    entity: Order | Route | null,
    provider: string,
    type: string,
  ) => void;
  onShare: (orderId: string, connectedWorkspaceId: string) => void;
  routedOrdersMap: Map<string, Order>;
  routes: Route[];
  searchTerm: string;
  unroutedOrders: Order[];
}

const List = memo(function List({
  appsMap,
  assigneesMap,
  autoAssignSearchingIds,
  groupParameter,
  isAppsLoading,
  isAssigneesLoading,
  isLoading,
  onCopy,
  onDelete,
  onShare,
  routedOrdersMap,
  routes,
  searchTerm,
  unroutedOrders,
  onSelectExternalProvider,
}: ListProps) {
  const viewProps = {
    appsMap,
    assigneesMap,
    autoAssignSearchingIds,
    isAppsLoading,
    isAssigneesLoading,
    isLoadingOrders: isLoading,
    onSelectExternalProvider,
    routedOrdersMap,
    routes,
    searchTerm,
    unroutedOrders,
  };

  const connectionsViewProps = {
    onCopy,
    onDelete,
    onShare,
  };

  if (
    routes.length == 0 &&
    routedOrdersMap.size == 0 &&
    unroutedOrders.length == 0 &&
    !isLoading &&
    groupParameter.value !== "connections-drivers"
  ) {
    return <EmptyState />;
  }

  if (groupParameter.value === "none") {
    return <ListView {...viewProps} onShare={onShare} />;
  }

  if (groupParameter.value === "states") {
    return <StatesView {...viewProps} onShare={onShare} />;
  }

  if (groupParameter.value === "locations") {
    return <LocationsView {...viewProps} onShare={onShare} />;
  }

  if (groupParameter.value === "connections-drivers") {
    return <ConnectionsDriversView {...viewProps} {...connectionsViewProps} />;
  }

  if (groupParameter.value === "connections") {
    return <ConnectionsView {...viewProps} {...connectionsViewProps} />;
  }

  return null;
});

export default List;
